import React, { useMemo, useState } from 'react';
import { Typography, Collapse, UnfoldMoreOutlinedIcon, UnfoldLessOutlinedIcon } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { MemberType, APIReferenceMathOperation } from '@modules/client';
import { TCompileTree } from '@modules/markdownProcessor/markdownProcessor';
import { ApiTag, EngineTag } from '@modules/ui/tags/types';
import { getMemberSlug } from '@modules/apiReference/page/utils/apiReferenceUtils';
import { getMemberTitle } from '@modules/apiReference/page/utils/apiReferencePageNavUtils';
import CodeSample from '@modules/utilities/interfaces/CodeSample';
import EngineMarkdown from '@modules/ui/markdown/components/EngineMarkdown';
import FeedbackButton from '@modules/feedback/components/FeedbackButton';
import { useRobloxAuthentication } from '@rbx/auth';
import { useShareFeedbackProvider } from '@modules/feedback/context/ShareFeedbackProvider';
import { splitDeprecatedTags } from '../utils/tagUtils';
import getReferenceMemberIcon from './getReferenceMemberIcon';
import ReferenceCodeSamples from './ReferenceCodeSamples';
import ReferenceMemberParametersTable from './ReferenceMemberParametersTable';
import ReferenceMemberReturnsTable from './ReferenceMemberReturnsTable';
import ReferenceMemberTags from './ReferenceMemberTags';
import ReferenceMemberSignature, { ReferenceMemberSignatureData } from './ReferenceMemberSignature';
import useReferenceMemberSignatureStyles from './ReferenceMemberSignature.styles';
import LinkableTypeReference from './LinkableTypeReference';
import ReferenceDeprecationWarning from './ReferenceDeprecationWarning';
import useReferenceMemberDetailContentStyles from './ReferenceMemberDetailContent.styles';
import ReferenceDivider from './ReferenceDivider';

type ReferenceMemberDetailContentProps = {
  name: string;
  summary: string | TCompileTree | null | undefined;
  description: string | TCompileTree | null | undefined;
  deprecationMessage: string | null | undefined;
  member: ReferenceMemberSignatureData | APIReferenceMathOperation;
  memberType: MemberType;
  allTags: EngineTag[];
  codeSamples?: CodeSample[] | null;
  id?: string;
};

function ReferenceMemberDetailContent({
  name,
  summary,
  deprecationMessage,
  description,
  member,
  memberType,
  allTags,
  codeSamples,
  id,
}: ReferenceMemberDetailContentProps) {
  const { translate } = useTranslation();
  const { isFetched: isAuthLoaded } = useRobloxAuthentication();
  const { isReady: isAllowFeedbackCheckReady, isDocsFeedbackAllowed } = useShareFeedbackProvider();

  const { classes } = useReferenceMemberDetailContentStyles();
  const { classes: signatureClasses } = useReferenceMemberSignatureStyles();

  const icon = getReferenceMemberIcon(memberType);

  // Check if this is a math operation
  const isMathOp = memberType === MemberType.MathOperation;

  const hasParameters =
    'parameters' in member && Array.isArray(member.parameters) && member.parameters.length > 0;

  // Only show returns table for member types that can return values (methods, functions, callbacks)
  const shouldShowReturns =
    (memberType === MemberType.Method ||
      memberType === MemberType.Function ||
      memberType === MemberType.Callback) &&
    'returns' in member &&
    Array.isArray(member.returns) &&
    member.returns.length > 0;

  // Split tags into deprecated and non-deprecated
  const { hasDeprecatedTag: hasDeprecatedTags, nonDeprecatedTags } = useMemo(
    () => splitDeprecatedTags(allTags),
    [allTags],
  );

  // Member is deprecated if it has deprecated tag OR has deprecation message
  const isDeprecated = hasDeprecatedTags || !!deprecationMessage;

  // Deprecated members are collapsed by default
  const [isExpanded, setIsExpanded] = useState(!isDeprecated);

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const showDetailsText = translate('Label.ShowDetails') || 'Show details';
  const hideDetailsText = translate('Label.HideDetails') || 'Hide details';

  return (
    <div>
      {/* Title Section: Icon + Name + Deprecated Tag + Expand/Collapse Icon */}
      <div className={classes.titleSection}>
        {icon && <div className={classes.iconWrapper}>{icon}</div>}
        <Typography
          variant='h3'
          id={id ?? getMemberSlug(name)}
          className={isDeprecated ? classes.titleDeprecated : classes.title}>
          {isMathOp && 'typeA' in member && 'operation' in member && 'typeB' in member ? (
            <React.Fragment>
              <code>{member.typeA}</code> {member.operation} <code>{member.typeB}</code>
            </React.Fragment>
          ) : (
            getMemberTitle(name)
          )}
        </Typography>
        {isDeprecated && (
          <ReferenceMemberTags tags={[ApiTag.Deprecated]} memberName={name} engineRevampEnabled />
        )}
        {/* Expand/Collapse icon on the right */}
        {isDeprecated && (
          <button
            type='button'
            onClick={handleToggleExpand}
            className={classes.toggleIcon}
            aria-label={isExpanded ? hideDetailsText : showDetailsText}>
            {isExpanded ? hideDetailsText : showDetailsText}
            {isExpanded ? (
              <UnfoldLessOutlinedIcon fontSize='small' />
            ) : (
              <UnfoldMoreOutlinedIcon fontSize='small' />
            )}
          </button>
        )}
      </div>

      {/* Deprecation Warning */}
      {deprecationMessage && (
        <div className={classes.section}>
          <ReferenceDeprecationWarning deprecationMessage={deprecationMessage} />
        </div>
      )}

      <Collapse in={isExpanded} timeout='auto' unmountOnExit>
        {/* Summary Section */}
        {summary && (
          <div className={classes.section}>
            <EngineMarkdown>{summary}</EngineMarkdown>
          </div>
        )}

        {/* Non-Deprecated Tags Section */}
        {nonDeprecatedTags.length > 0 && (
          <div className={classes.section}>
            <ReferenceMemberTags tags={nonDeprecatedTags} memberName={name} engineRevampEnabled />
          </div>
        )}

        {/* Signature Section */}
        <div className={classes.section}>
          {isMathOp && 'typeA' in member && 'operation' in member && 'typeB' in member ? (
            <div className={signatureClasses.signatureContainer}>
              <LinkableTypeReference type={member.typeA} engineRevampEnabled />
              <span> {member.operation} </span>
              <LinkableTypeReference type={member.typeB} engineRevampEnabled />
              {member.returnType && (
                <React.Fragment>
                  <span> : </span>
                  <LinkableTypeReference type={member.returnType} engineRevampEnabled />
                </React.Fragment>
              )}
            </div>
          ) : (
            <ReferenceMemberSignature
              member={member as ReferenceMemberSignatureData}
              memberType={memberType}
            />
          )}
        </div>

        {/* Parameters Details (for methods/functions/constructors) */}
        {hasParameters && (
          <div className={classes.section}>
            <ReferenceMemberParametersTable parameters={member.parameters ?? null} />
          </div>
        )}

        {/* Returns Details (for methods/functions/callbacks) */}
        {shouldShowReturns && (
          <div className={classes.section}>
            <ReferenceMemberReturnsTable returns={member.returns ?? null} />
          </div>
        )}

        {/* Description Section */}
        {description && (
          <div className={classes.section}>
            <EngineMarkdown>{description}</EngineMarkdown>
          </div>
        )}

        {/* Code Samples */}
        {Array.isArray(codeSamples) && codeSamples.length > 0 && (
          <div className={classes.section}>
            <ReferenceCodeSamples codeSamples={codeSamples} />
          </div>
        )}

        <FeedbackButton
          pageTitle={name}
          show={!isAuthLoaded || !isAllowFeedbackCheckReady || isDocsFeedbackAllowed}
          engineRevampEnabled
          showProvideFeedbackText
        />
      </Collapse>

      <ReferenceDivider />
    </div>
  );
}

export default ReferenceMemberDetailContent;
